// Graphic 4 of 4 — healing-loop health: UNKNOWN share of failures per week.
// A falling line is the classifier learning (rules → memory → Glean → human);
// a rising one means the healing loop needs attention. Not a vanity metric:
// it is the plan's 10% → 0.28% proof, per week.
import { useState } from 'react';
import { useChart, tooltipStyle, axisStyle } from '../../lib/useChart';
import { chrome, classificationColor } from '../../lib/palette';
import { pct, esc } from '../../lib/format';
import { weekLabel } from '../../lib/trends';
import ChartCard from '../ChartCard';

export default function UnknownShare({ series, mode }) {
  const [showTable, setShowTable] = useState(false);
  const c = chrome(mode);
  // UNKNOWN's own fixed palette slot — the same hue its chip carries.
  const color = classificationColor('unknown', mode);

  const [ref] = useChart(
    () => ({
      backgroundColor: 'transparent',
      grid: { left: 40, right: 40, top: 12, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: c.axis, width: 1 } },
        ...tooltipStyle(c),
        formatter: (params) => {
          const w = series[params[0].dataIndex];
          const share = w.unknown_share;
          return (
            `<div style="font-weight:600;font-size:14px">${esc(share === null ? 'no failures' : pct(share))}</div>` +
            `<div style="color:${c.textSecondary}">UNKNOWN share · week of ${esc(weekLabel(w.week_start))} · ${esc(w.failures)} failure${w.failures === 1 ? '' : 's'}</div>`
          );
        },
      },
      xAxis: {
        type: 'category',
        data: series.map((w) => weekLabel(w.week_start)),
        boundaryGap: false,
        ...axisStyle(c),
        axisLabel: { color: c.muted, fontSize: 10 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: (v) => Math.min(100, Math.ceil((v.max || 1) * 1.2)),
        ...axisStyle(c),
        axisLine: { show: false },
        axisLabel: { color: c.muted, fontSize: 11, formatter: (v) => `${v}%` },
      },
      series: [
        {
          type: 'line',
          data: series.map((w) => w.unknown_share),
          // Weeks with zero failures have no share — an honest gap, not a zero.
          connectNulls: false,
          smooth: false,
          lineStyle: { width: 2, color, cap: 'round', join: 'round' },
          itemStyle: { color, borderColor: c.surface, borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 7,
          label: {
            show: true,
            position: 'right',
            color: c.textPrimary,
            fontSize: 12,
            fontWeight: 600,
            formatter: (p) => (p.dataIndex === series.length - 1 && p.value != null ? pct(p.value) : ''),
          },
        },
      ],
    }),
    [series],
    { mode }
  );

  return (
    <ChartCard
      title="Healing-loop health — UNKNOWN share"
      subtitle="share of the week's failures still unclassified · falling = the classifier is learning"
      showTable={showTable}
      onToggleTable={() => setShowTable((v) => !v)}
      table={
        <table className="data-table">
          <thead>
            <tr><th>Week of</th><th className="num">UNKNOWN share</th><th className="num">Failures</th></tr>
          </thead>
          <tbody>
            {series.map((w) => (
              <tr key={w.week_start}>
                <td>{weekLabel(w.week_start)}</td>
                <td className="num">{w.unknown_share === null ? '—' : pct(w.unknown_share)}</td>
                <td className="num">{w.failures}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="chart chart--half" />
    </ChartCard>
  );
}
